import { useRouteLocale } from "@/hooks/useRouteLocale";
import { lazy, Suspense, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { Card, CardContent } from "@/components/ui/card";
import { getCookieConsent, hasPaymentCookieConsent, setCookieConsent } from "./CookieConsentBanner";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PUBLIC_ROUTES } from "@/routes";

const PaymentPage = lazy(() => import("@/pages/public/PaymentPage"));

/**
 * Renders the payment route only when the user has accepted payment cookies.
 * PaymentPage is lazy-loaded so @stripe/stripe-js is never imported until consent is given
 * (stripe-js runs a script-injection side effect on import).
 * When consent is missing, shows the same page layout as PaymentPage with an inline consent card
 * so the flow feels continuous rather than an empty gated view.
 */
export function PaymentRouteGate() {
  const { t } = useTranslation();
  const locale = useRouteLocale();
  const navigate = useNavigate();
  const hasConsent = hasPaymentCookieConsent();
  const [, forceUpdate] = useState(0);
  const currentLocale = locale || "ro";
  // The gate owns the document title so the consent card and the checkout share it (JUB-195).
  usePageMeta({
    title: t("payment.metaTitle"),
    description: t("payment.subtitle"),
    locale: currentLocale,
    path: PUBLIC_ROUTES.payment(currentLocale),
    noindex: true,
  });

  const handleAccept = () => {
    const existing = getCookieConsent();
    setCookieConsent({
      essential: true,
      analytics: existing?.analytics ?? false,
      marketing: existing?.marketing ?? false,
      timestamp: Date.now(),
    });
    forceUpdate((n) => n + 1);
  };

  if (!hasConsent) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-4">
            <div className="flex h-16 items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="font-logo text-3xl font-bold bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
                  {t("header.logo")}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <LanguageSwitcher compact />
              </div>
            </div>
          </div>
        </header>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] py-12 px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-4xl space-y-8">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-foreground">
                {t("payment.title")}
              </h1>
              <p className="mt-2 text-muted-foreground">
                {t("payment.subtitle")}
              </p>
            </div>
            <Card className="border-2">
              <CardContent className="pt-6">
                <p className="text-muted-foreground mb-4">
                  {t("cookies.paymentConsent.paymentMessage")}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={handleAccept}>
                    {t("cookies.paymentConsent.acceptAndContinue")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/${currentLocale}/register`)}
                  >
                    {t("common.back")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
        </div>
      }
    >
      <PaymentPage />
    </Suspense>
  );
}
